import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Spinner, Badge, Button } from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { formatRoleName, workstatusOptions } from '../constants/utils';
import api from '../helpers/api';
import AssignTechnicianModal from './AssignTechnicianModal'; // We'll create this component

const TicketDetailsModal = ({
  isOpen,
  toggle,
  ticketData,
  loading,
  formatCreatedDate,
  onStatusChange,
  fetchAssetIssueFromOrganization
}) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [showAssignTechnicianModal, setShowAssignTechnicianModal] = useState(false);
  const [pendingStatusSelected, setPendingStatusSelected] = useState(false);

  useEffect(() => {
    if (ticketData?.status) {
      setSelectedStatus(ticketData.status);
    }
  }, [ticketData]);

  useEffect(() => {
    if (ticketData?.id) {
      getActivity();
    }
  }, [ticketData?.id]);

  const getStatusBadge = (status) => {
    const badgeClass = {
      SUBMITTED: 'bg-info',
      IN_PROGRESS: 'bg-warning',
      RESOLVED: 'bg-success',
      ON_HOLD: 'bg-danger',
      PENDING: 'bg-secondary',
    }[status] || 'bg-secondary';

    const labelMap = {
      SUBMITTED: 'Open',
      IN_PROGRESS: 'In Progress',
      RESOLVED: 'Resolved',
      ON_HOLD: 'On Hold',
      PENDING: 'Pending',
    };

    return (
      <Badge
        className={`status-badge text-white ${badgeClass}`}
        style={{
          fontSize: '0.8rem',
          padding: '0.35rem 0.65rem',
          borderRadius: '0.5rem',
        }}
      >
        {labelMap[status] || status?.replace(/_/g, ' ') || 'N/A'}
      </Badge>
    );
  };

  const getActivity = async () => {
    try {
      setLoadingActivities(true);
      const { data } = await api.get(`asset-issues/activity?assertIssueId=${ticketData?.id}`);
      setActivities(data.response || []);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const formatActivityText = (activity) => {
    // Replace 'submitted' with 'open' in activity text
    return activity?.activity?.replace(/submitted/gi, 'Open');
  };

  const handleStatusClick = async(value) => {
    if (value === 'PENDING') {
      setPendingStatusSelected(true);
      setShowAssignTechnicianModal(true);
    } else {
      setSelectedStatus(value);
      if (value !== ticketData.status) {
        await onStatusChange?.(ticketData, value);
        await getActivity();
      }
    }
  };

  const handleAssignTechnician = async (id,technicianId) => {
    try {
      // First update the status to PENDING
      await onStatusChange?.(ticketData, 'PENDING');
      setSelectedStatus('PENDING');
      const payload = { id: id, assignedTechnician: technicianId };
      // Then assign the technician (you'll need to implement this API call)
      await api.put(`asset-issues/update`, payload);
      
      // Refresh activities
      await getActivity();
      await fetchAssetIssueFromOrganization();
      
      // Close the modal
      setShowAssignTechnicianModal(false);
      setPendingStatusSelected(false);
    } catch (error) {
      console.error('Error assigning technician:', error);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
        centered
        className="fade-scale-modal"
        style={{ maxHeight: '85vh' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
        >
          <div
            className="modal-header"
            style={{
              position: 'sticky',
              top: 0,
              background: 'white',
              zIndex: 2,
            }}
          >
            <h5 className="modal-title">🎫 Ticket - {ticketData?.ticketNumber}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={toggle}
              aria-label="Close"
            />
          </div>

          <div
            className="modal-body"
            style={{
              display: 'flex',
              flexDirection: 'column',
              paddingTop: '0',
              maxHeight: 'calc(85vh - 60px)',
              overflow: 'auto',
            }}
          >
            {/* Editable Status Selector */}
            <div className="px-4 py-3">
              <h6 className="mb-3 fw-semibold text-muted">Edit Status</h6>
              <div className="d-flex gap-3 flex-wrap">
                {workstatusOptions.map((item) => {
                  const isActive = selectedStatus === item.value;
                  return (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      key={item.value}
                      className={`d-flex align-items-center gap-2 px-3 py-2 rounded border shadow-sm transition ${
                        isActive ? item.colorClass : 'bg-light text-dark'
                      }`}
                      style={{
                        borderWidth: isActive ? '2px' : '1px',
                        borderColor: isActive ? item.borderColor : '#ccc',
                        minWidth: '120px',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleStatusClick(item.value)}
                    >
                      <FeatherIcon icon={item.icon} size={16} />
                      <span className="fw-medium">{item.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Assign Technician Button */}
            <div className="px-4 pb-3">
              <Button
                color="primary"
                outline
                className="d-flex align-items-center gap-2"
                onClick={() => setShowAssignTechnicianModal(true)}
              >
                <FeatherIcon icon="user-plus" size={16} />
                Assign Technician
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                <Row className="mb-4 px-4">
                  <Col md={6}>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">Ticket Number:</span>
                      <div className="text-dark">{ticketData?.ticketNumber}</div>
                    </div>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">Status:</span>
                      <div>{getStatusBadge(selectedStatus)}</div>
                    </div>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">Created At:</span>
                      <div>{formatCreatedDate(ticketData?.createdAt)}</div>
                    </div>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">First Occurrence:</span>
                      <div>{formatCreatedDate(ticketData?.firstOccurrence)}</div>
                    </div>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">Symptom:</span>
                      <div>
                        {ticketData?.symptomsTags?.length > 0
                          ? ticketData.symptomsTags.map(tag => tag.replace(/_/g, ' ')).join(', ')
                          : 'N/A'}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">Impact on Care:</span>
                      <div>{ticketData?.impactOnCare?.replace(/_/g, ' ') || 'N/A'}</div>
                    </div>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">Issue Type:</span>
                      <div>
                        {ticketData?.issueType?.length > 0
                          ? ticketData?.issueType?.map(tag => tag.replace(/_/g, ' ')).join(', ')
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">Urgency Level:</span>
                      <div>{ticketData?.urgencyLevel?.replace(/_/g, ' ') || 'N/A'}</div>
                    </div>
                    <div className="mb-3">
                      <span className="fw-semibold text-muted">Usage Status:</span>
                      <div>{ticketData?.usageStatus?.replace(/_/g, ' ') || 'N/A'}</div>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-4 px-4">
                  <Col md={6}>
                    <h6 className="mb-2">Asset Details</h6>
                    <div className="mb-2">
                      <strong>Name:</strong> {ticketData?.asset?.assetName || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Department:</strong> {ticketData?.asset?.assetCategory || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Serial Number:</strong> {ticketData?.asset?.serialNumber || 'N/A'}
                    </div>
                  </Col>
                  <Col md={6}>
                    <h6 className="mb-2">Location Details</h6>
                    <div className="mb-2">
                      <strong>Organization:</strong> {ticketData?.organization?.name || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Location:</strong> {ticketData?.asset?.currentLocation || ticketData?.locationName || 'N/A'}
                    </div>
                  </Col>
                </Row>

                   <Row className="mb-4 px-4">
                  <Col md={4}>
                    <h6 className="mb-2">Requester Details</h6>
                    <div className="mb-2">
                      <strong>Name:</strong> {ticketData?.reportedBy?.name || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Role:</strong> {formatRoleName(ticketData?.reportedBy?.role) || 'N/A'}
                    </div>
                  </Col>
                  <Col md={4}>
                    <h6 className="mb-2">Preferred Techinician</h6>
                    <div className="mb-2">
                      <strong>Organization:</strong> {ticketData?.preferredTechnician?.name || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Location:</strong> {formatRoleName(ticketData?.preferredTechnician?.role) || 'N/A'}
                    </div>
                  </Col>
                     <Col md={4}>
                    <h6 className="mb-2">Assigned Technician</h6>
                    <div className="mb-2">
                      <strong>Name:</strong> {ticketData?.assignedTechnician?.name || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Role:</strong> {formatRoleName(ticketData?.assignedTechnician?.role) || 'N/A'}
                    </div>
                  </Col>
                </Row>

                {ticketData?.description && (
                  <Row className="mb-4 px-4">
                    <Col md={12}>
                      <h6>Description</h6>
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border p-3 rounded bg-white shadow-sm"
                      >
                        {ticketData?.description}
                      </motion.div>
                    </Col>
                  </Row>
                )}

                {/* Activity Log Section */}
                <Row className="px-4 mb-4">
                  <Col md={12}>
                    <h6 className="mb-3">Activity Log</h6>
                    {loadingActivities ? (
                      <div className="text-center py-3">
                        <Spinner color="primary" size="sm" />
                      </div>
                    ) : activities.length > 0 ? (
                      <div className="timeline">
                        {activities.map((activity, index) => (
                          <div key={activity.id} className="timeline-item">
                            <div className="timeline-point"></div>
                            <div className="timeline-content">
                              <div className="d-flex justify-content-between">
                                <div>
                                  <strong>{activity.userName}</strong>
                                  <p className="mb-1">
                                    {formatActivityText(activity)}
                                  </p>
                                </div>
                                <small className="text-muted">
                                  {formatCreatedDate(activity?.createdAt)}
                                </small>
                              </div>
                              {index < activities.length - 1 && <div className="timeline-line"></div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">No activity recorded</div>
                    )}
                  </Col>
                </Row>
              </>
            )}
          </div>
        </motion.div>
      </Modal>

      {/* Assign Technician Modal */}
      <AssignTechnicianModal
        isOpen={showAssignTechnicianModal}
        toggle={() => {
          setShowAssignTechnicianModal(false);
          setPendingStatusSelected(false);
        }}
        ticketId={ticketData?.id}
        onAssign={handleAssignTechnician}
        isPendingStatus={pendingStatusSelected}
      />
    </>
  );
};

export default TicketDetailsModal;